import { useCallback, useMemo } from "react";
import {
  type FieldValues,
  type Path,
  useFormContext,
  useFormState,
  useWatch,
} from "react-hook-form";
import type {
  FormApi,
  FormValidationFailureHandler,
  FormValidator,
} from "./types";
import { validateBeforeSubmit } from "./validateBeforeSubmit";

/**
 * Synthesize a stable FormApi object from the surrounding RHF FormProvider.
 *
 * Used internally by FormSlidePanel and WizardSlidePanel to expose a
 * Kendo-Form-compatible surface to children while running RHF underneath.
 *
 * Subscribes broadly here because consumers reading `formApi.values` expect
 * up-to-date state on every change. Per-field render perf is achieved by the
 * predefined Form* components (which use useController and only re-render
 * their own field on unrelated changes).
 *
 * Method references (onChange, setProgrammaticValue, etc.) are stabilized via
 * useCallback so useEffects that list them in dependency arrays do not loop.
 */
export function useFormApi<TValues extends FieldValues>(
  onSubmit: (values: TValues) => void,
  validate?: FormValidator<TValues>,
  onValidationFailure?: FormValidationFailureHandler<TValues>,
): FormApi<TValues> {
  const methods = useFormContext<TValues>();
  const { setValue, getValues, reset } = methods;
  const values = useWatch<TValues>() as TValues;
  const { dirtyFields, touchedFields, isValid, errors } =
    useFormState<TValues>();

  const valueGetter = useCallback(
    <T = unknown>(name: string) => getValues(name as Path<TValues>) as T,
    [getValues],
  );

  const onChange = useCallback(
    (name: string, { value }: { value: unknown }) => {
      setValue(name as Path<TValues>, value as never, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    [setValue],
  );

  const setProgrammaticValue = useCallback(
    (name: string, value: unknown) => {
      setValue(name as Path<TValues>, value as never, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    },
    [setValue],
  );

  const setProgrammaticValues = useCallback(
    (vals: Partial<TValues>) => {
      const current = getValues();
      // Deliberately NOT `keepIsValid`. RHF latches that option onto the form
      // instance (`_state.keepIsValid`) and never clears it, and `_setValid()`
      // short-circuits on it — so one seeding reset would permanently freeze
      // `isValid`, leaving anything gated on `formApi.valid` (the Settings tab's
      // Save button) stuck at whatever it read before the server data landed.
      // `keepErrors` still carries any error already on screen across the reset.
      reset({ ...current, ...vals } as TValues, {
        keepDirty: true,
        keepDirtyValues: true,
        keepTouched: true,
        keepErrors: true,
        keepIsSubmitted: true,
        keepSubmitCount: true,
      });
    },
    [getValues, reset],
  );

  const handleSubmit = useCallback(() => {
    void (async () => {
      const valid = await validateBeforeSubmit(
        methods,
        validate,
        onValidationFailure,
      );
      if (!valid) return;
      onSubmit(getValues() as TValues);
    })();
  }, [methods, getValues, onSubmit, onValidationFailure, validate]);

  const onFormReset = useCallback(() => reset(), [reset]);

  const markPristine = useCallback(
    () => reset(getValues()),
    [reset, getValues],
  );

  return useMemo<FormApi<TValues>>(
    () => ({
      values,
      valueGetter,
      modified: dirtyFields as Record<string, boolean>,
      touched: touchedFields as Record<string, boolean>,
      valid: isValid,
      errors: flattenErrors(errors),
      onChange,
      setProgrammaticValue,
      setProgrammaticValues,
      onSubmit: handleSubmit,
      onFormReset,
      markPristine,
    }),
    [
      values,
      valueGetter,
      dirtyFields,
      touchedFields,
      isValid,
      errors,
      onChange,
      setProgrammaticValue,
      setProgrammaticValues,
      handleSubmit,
      onFormReset,
      markPristine,
    ],
  );
}

function flattenErrors(
  errors: Record<string, unknown>,
): Record<string, string | undefined> {
  const out: Record<string, string | undefined> = {};
  for (const [key, val] of Object.entries(errors)) {
    if (val && typeof val === "object" && "message" in val) {
      out[key] = (val as { message?: string }).message;
    }
  }
  return out;
}
